import {
    flattenPaginationRequest,
    internalApiSlice,
    PaginationRequest,
    PaginationResponse,
    transformResponse
} from "@redux/api/internalApiSlice";
import { Me, Users } from "@/types/general";


export const usersApi = internalApiSlice.injectEndpoints({
    endpoints: (builder) => ({
        getUsersFull: builder.query<
            PaginationResponse<Users.Root>,
            PaginationRequest<Users.DTO.Filters, Users.DTO.OrderBy>
        >({
            query: (req) => ({
                url: `user`,
                method: "GET",
                params: {
                    ...flattenPaginationRequest(req),
                },
                parseDates: ["last_seen"]
            }),
            providesTags: (result) => result?.payload?.length ? [
                ...result.payload.map(x => ({ type: "User", id: x.id })),
                { type: "User", id: "LIST" }
            ] : [{ type: "User", id: "LIST" }],
        }),

        getMe: builder.query<Me.Root, void>({
            query: () => ({
                url: `me`
            }),
            providesTags: [{ type: "Me", id: "LIST" }],
            transformResponse
        }),

        getUserData: builder.query<Users.Root, number>({
            query: (id) => ({ url: `user/${id}` })
        }),

        getCompanyInvitations: builder.query<Users.DTO.CompanyInvitations[], void>({
            query: () => ({
                url: `invitations`,
                parseDates: ["invited_at", "last_sent_at"]
            }),
            providesTags: (result, error, page) =>
                result
                    ? [
                        ...result.map(({ id }) => ({ type: "InviteUser" as const, id })),
                        { type: "InviteUser", id: "LIST" }
                    ]
                    : [{ type: "InviteUser", id: "LIST" }]
        }),

        inviteUser: builder.mutation<Users.Root, Users.DTO.InviteUser>({
            query: (body) => ({
                url: `invitations`,
                method: "POST",
                data: body
            }),
            transformResponse,
            invalidatesTags: [{ type: "InviteUser", id: "LIST" }]

            // async onQueryStarted(body, { dispatch, queryFulfilled }) {
            //     try {
            //         const { data: newUser } = await queryFulfilled;
            //         const createResult = dispatch(usersApi.util.updateQueryData("getUsers", undefined, (draft) => {
            //             draft.push({ ...newUser })
            //         }));
            //     } catch (err) {
            //         console.error(err);
            //     }
            // }
        }),

        resendInvite: builder.mutation<void, number>({
            query: (invitation_id) => ({
                url: `invitations/${invitation_id}/resend`,
                method: "PUT"
            }),
            invalidatesTags: [{ type: "InviteUser", id: "LIST" }]
        }),

        revokeInvite: builder.mutation<void, number>({
            query: (invitation_id) => ({
                url: `invitations/${invitation_id}/revoke`,
                method: "DELETE"
            }),
            invalidatesTags: [{ type: "InviteUser", id: "LIST" }]
        }),

        getInvite: builder.query<Users.DTO.Invite, string>({
            query: (token_id) => ({
                url: `invitations/data/${token_id}`,
            }),
        }),

        getInvitationsLimits: builder.query<Users.DTO.InviteLimits, void>({
            query: () => ({
                url: `invitations/limits`,
            }),
        }),

        acceptNewUserInvite: builder.mutation<void, Users.DTO.AcceptInvite & {token_id: string}>({
            query: ({token_id, ...body}) => ({
                url: `invitations/register-with-token/${token_id}`,
                method: "POST",
                data: body
            }),
        }),

        acceptExistingUserInvite: builder.mutation<void, string>({
            query: (token_id) => ({
                url: `invitations/accept/${token_id}`,
                method: "POST"
            }),
        }),

        updateUser: builder.mutation<Users.Root, Users.DTO.Update>({
            query: (body) => ({
                url: `me`,
                method: "PATCH",
                data: body
            }),
            transformResponse,
            invalidatesTags: [{ type: "User", id: "LIST" }]

            // async onQueryStarted(body, { dispatch, queryFulfilled }) {
            //     try {
            //         const { data: updatedUser } = await queryFulfilled;
            //         const updateResult = dispatch(usersApi.util.updateQueryData("getUsers", undefined, (draft) => {
            //             const index = draft.findIndex(item => item.id === updatedUser.id)
            //             if (index !== -1) draft[index] = updatedUser
            //         }))
            //     } catch (err) {
            //         console.error(err)
            //     }
            // }
        }),

        updateUserPassword: builder.mutation<void, Users.DTO.UpdatePassword>({
            query: (body) => ({
                url: `me/password`,
                method: "PUT",
                data: body
            })
        }),

        suspendUser: builder.mutation<void, number>({
            query: (userId) => ({
                url: `user/block/${userId}`,
                method: "PUT"
            }),
            invalidatesTags: [{ type: "User", id: "LIST" }]
        }),

        deleteUser: builder.mutation<void, number>({
            query: (id) => ({
                url: `user/${id}`,
                method: "DELETE"
            }),
            invalidatesTags: [{ type: "User", id: "LIST" }]
            // async onQueryStarted(id, { dispatch, queryFulfilled }) {
            //     try {
            //         const { data: deleted } = await queryFulfilled;
            //         const createResult = dispatch(usersApi.util.updateQueryData("getUsers", undefined, (draft) => {
            //             const index = draft.findIndex(item => item.id === id);
            //             if (index !== -1) {
            //                 draft.splice(index, 1);
            //             }
            //         }));
            //     } catch (err) {
            //         console.error(err);
            //     }
            // }
        })
    })
});

export const {
    useGetUsersFullQuery,
    useGetMeQuery,
    useGetUserDataQuery,
    useInviteUserMutation,
    useResendInviteMutation,
    useRevokeInviteMutation,
    useGetCompanyInvitationsQuery,
    useGetInviteQuery,
    useGetInvitationsLimitsQuery,
    useAcceptNewUserInviteMutation,
    useAcceptExistingUserInviteMutation,
    useUpdateUserMutation,
    useUpdateUserPasswordMutation,
    useSuspendUserMutation,
    useDeleteUserMutation
} = usersApi;